import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import type { OnChangeFn, SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import MonthPicker from '@/components/ui/MonthPicker'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import {
  useIndustryHoldings,
  useMonthlyHoldings,
  useSectorCompanies,
  useStockHolders,
  useSuspenseFundPeriods,
} from '@/features/mutual-funds/api/queries'
import {
  canonicalPeriod,
  clampPage,
  compareSlicesByValueDesc,
  knownPeriod,
  sectorOrAll,
} from '@/features/mutual-funds/lib/defaults'
import { sectorSliceLabel } from '@/features/mutual-funds/lib/format'
import { useOnline } from '@/hooks/useOnline'
import { cn } from '@/lib/utils'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { SectorSlice } from '@/schemas/mutual-funds'
import { toPageSize } from '@/schemas/table'
import CompaniesTable, {
  type IndustryCompany,
} from '@/screens/mutual-funds/components/industry-holdings/CompaniesTable'
import HoldersDonutCard from '@/screens/mutual-funds/components/industry-holdings/HoldersDonutCard'
import SectorDonutCard from '@/screens/mutual-funds/components/industry-holdings/SectorDonutCard'
import SectorSelect, {
  ALL_SECTORS,
} from '@/screens/mutual-funds/components/industry-holdings/SectorSelect'
import StockHoldersDialog from '@/screens/mutual-funds/components/industry-holdings/StockHoldersDialog'
import {
  industryHoldingsSearchSchema,
  loadIndustryHoldings,
} from '@/screens/mutual-funds/IndustryHoldingsScreen.loader'

const TITLE = 'Industry Holdings'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title={TITLE} />

/** `pagingParams`'s own `pageSize` default (20) — a shorter sector's page is
 *  narrower than this skeleton, never taller. */
const SKELETON_ROWS = 20

/**
 * Built from the loaded page's own boxes, in the order the loaded page draws them: the donut card
 * (20 inset, a 24 head, the 200 ring beside its legend 16 under it), 24 down, then the controls
 * row (the 280 search, then at the right the 192 sector select and the 140 month trigger —
 * `h-11` below `lg` where the search is a touch target, `h-9` from it), the four-column
 * companies table and its pager, 16 apart.
 */
function IndustryHoldingsPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading industry holdings…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <div className={cn(SECTION_CARD, '@container [--section-card-pad-y:var(--space-5)]')}>
        <div className="flex h-6 items-center justify-between gap-3">
          <Skeleton className="h-4 w-56 rounded-xs" />
          <Skeleton className="h-3 w-28 rounded-xs" />
        </div>
        <div className="mt-4 flex flex-col items-center gap-4 @xl:flex-row @xl:gap-10">
          <Skeleton className="size-50 shrink-0 rounded-full" />
          <div className="grid w-full flex-1 gap-3">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-5 w-full" />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-11 w-full rounded-sm sm:w-70 lg:h-9" />
          <span className="hidden flex-1 sm:block" />
          <Skeleton className="h-11 w-48 lg:h-9" />
          <Skeleton className="h-9 w-35" />
        </div>
        <div>
          <DataTableSkeleton rows={SKELETON_ROWS} columns={4} rowHeight="lg" />
          <div className="mt-6 flex h-11 items-center justify-between gap-3 lg:h-8">
            <Skeleton className="h-4 w-36 rounded-xs" />
            <Skeleton className="h-4 w-44 rounded-xs" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

function IndustryHoldingsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Industry holdings did not load" />
}

export const Route = createFileRoute('/_app/mutual-funds/industry-holdings')({
  validateSearch: industryHoldingsSearchSchema,
  loader: loadIndustryHoldings,
  pendingComponent: IndustryHoldingsPending,
  errorComponent: IndustryHoldingsError,
  component: IndustryHoldingsScreen,
})

/** The search box's match: a symbol or a name, case-blind, over the companies already in hand. */
const matchesCompany = (row: IndustryCompany, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (needle === '') return true
  return (
    row.symbol.toLowerCase().includes(needle) || (row.name?.toLowerCase().includes(needle) ?? false)
  )
}

/**
 * The one company the search names (the mock's rule): the typed symbol exactly, else the only
 * company the search left. Null for none or several.
 */
const namedCompany = (rows: readonly IndustryCompany[], term: string): string | null => {
  const needle = term.trim().toLowerCase()
  if (needle === '') return null
  const exact = rows.find((row) => row.symbol.toLowerCase() === needle)
  if (exact !== undefined) return exact.symbol
  return rows.length === 1 ? (rows[0]?.symbol ?? null) : null
}

/**
 * Mutual funds · Industry holdings — one holdings period's sector split (the donut), a controls
 * row under it, and the companies the funds hold: in every sector (the opening view, the old
 * Monthly holdings page, folded in here by the 2026-11-03 mock) or in one.
 *
 * THE CONTROLS (the 2026-11-03 mock): the search filters the table as it is typed; the sector
 * select ("All sectors" first) and the month picker apply at once, writing `?sector=&period=`
 * (page 1) — the Filter button is gone. A click on a donut slice or legend row picks its sector,
 * and a second click on it goes back to All sectors (the unclassified slice has no code, so it
 * picks nothing). The URL is the one source of truth.
 *
 * THREE KEYED READS, each `useQuery` + `keepPreviousData` per docs/route-loading.md ("Search
 * params" — main data keyed on a search param is never suspense): the sector split, keyed on
 * `period`; and the table, which is `/monthly-holdings/` keyed on `period` for All sectors, or
 * `/companies/` keyed on `(sector, period)` for one. Each sits in its own `RefreshRegion` while a
 * change is in flight, sharing one `UpdatingNote` in the controls row.
 *
 * `periods` (the month picker's months) is unkeyed and suspense-safe: the loader awaits it, so it
 * is always warm here. Only a label it lists is ever sent (`knownPeriod`): a hand-edited
 * `?period=` it lacks would be a 400 `MUTUAL_FUND_PERIOD_UNKNOWN`, and reads as the latest.
 *
 * The table's key MUST match what the loader prefetched exactly: `knownPeriod(search.period)` (the
 * URL's own value), never `holdings.period` (the backend's RESOLVED label), or it would open a
 * second cache entry the loader never warmed. A `?sector=` naming no classified slice of the
 * period reads as All sectors (`sectorOrAll`), in the loader and here alike.
 *
 * The table's sort is local state, applied to the whole list before it is paged; a sort or a new
 * search goes back to page 1.
 *
 * ONE COMPANY'S FUNDS (mm-be #372, `/mutual-funds/holders/{symbol}/`): a search that names one
 * company swaps the donut for "Who holds X", and a row's Funds count opens "N funds hold X".
 * Before that endpoint is live it answers 404 (read as `null`), so the donut stays the sector
 * split and the count stays plain text — the count asks one company's holders (the search's, or
 * the whole table's first row, so typing does not ask again) to learn which it is.
 */
function IndustryHoldingsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()

  const { data: periods } = useSuspenseFundPeriods()

  const periodParam = knownPeriod(search.period, periods)
  const holdingsQuery = useIndustryHoldings(periodParam)
  const holdingsView = resolveViewState(holdingsQuery, {
    online,
    isEmpty: (data) => data.sectors.length === 0,
  })
  const holdings = holdingsQuery.data
  const sortedSectors =
    holdings === undefined ? [] : [...holdings.sectors].sort(compareSlicesByValueDesc)
  const appliedPeriod = periodParam ?? holdings?.period ?? periods[0]?.label ?? null

  // While the split still shows the OLD period's placeholder sectors, a `?sector=` cannot be
  // checked against this period yet: keep the URL's own pick rather than flashing All sectors.
  const sector = holdingsQuery.isPlaceholderData
    ? (search.sector ?? null)
    : sectorOrAll(search.sector, sortedSectors)
  const allSectors = sector === null

  const sectorQuery = useSectorCompanies(sector ?? '', periodParam)
  const allQuery = useMonthlyHoldings(periodParam, { enabled: allSectors })
  const companiesQuery = allSectors ? allQuery : sectorQuery
  const companiesView = allSectors
    ? resolveViewState(allQuery, { online, isEmpty: () => false })
    : resolveViewState(sectorQuery, { online, isEmpty: () => false })
  const companiesInHand: readonly IndustryCompany[] = allSectors
    ? (allQuery.data?.companies ?? []).map((row) => ({ kind: 'all' as const, ...row }))
    : (sectorQuery.data ?? []).map((row) => ({ kind: 'sector' as const, ...row }))

  const isUpdating = holdingsQuery.isPlaceholderData || companiesQuery.isPlaceholderData

  const [term, setTerm] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])

  const toFirstPage = () => {
    if (search.page !== 1) void navigate({ search: (prev) => ({ ...prev, page: 1 }) })
  }
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater)
    toFirstPage()
  }
  const pickSector = (code: string | null) => {
    void navigate({ search: (prev) => ({ ...prev, sector: code ?? undefined, page: 1 }) })
  }
  const pickPeriod = (period: string) => {
    void navigate({
      search: (prev) => ({ ...prev, period: canonicalPeriod(period, periods), page: 1 }),
    })
  }

  const companies = companiesInHand.filter((row) => matchesCompany(row, term))
  const searching = term.trim() !== ''
  const named = namedCompany(companies, term)
  const holders = useStockHolders(named ?? companiesInHand[0]?.symbol ?? '', periodParam)
  const holdersLive = holders.data != null
  const namedHolders =
    named !== null && holders.data?.symbol === named && holders.data.funds.length > 0
      ? holders.data
      : null
  const [holdersOf, setHoldersOf] = useState<string | undefined>(undefined)
  const page = clampPage(search.page, companies.length, search.pageSize)

  const selectedSlice = sortedSectors.find((slice) => slice.sector === sector)
  const sectorLabel = allSectors
    ? 'every sector'
    : selectedSlice === undefined
      ? 'this sector'
      : sectorSliceLabel(selectedSlice)

  const onPin = (slice: SectorSlice) => {
    if (slice.sector === null) return
    pickSector(slice.sector === sector ? null : slice.sector)
  }

  const holdingsFailed = holdingsView === 'failed' || holdingsView === 'offline'
  const holdingsEmpty = holdingsView === 'empty'

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      {namedHolders !== null ? (
        <HoldersDonutCard holders={namedHolders} />
      ) : holdingsFailed || holdingsEmpty ? null : (
        <RefreshRegion updating={holdingsQuery.isPlaceholderData}>
          <SectorDonutCard sectors={sortedSectors} pinned={sector} onPin={onPin} />
        </RefreshRegion>
      )}

      {/* The controls and the companies they pick are one block, 16 apart: the pages revamp's one
          gap from a toolbar to its first block, not the column's 24 between blocks. The controls
          stay up whatever the split did, so a period with no filings can be left from here. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            aria-label="Company or symbol"
            placeholder="Search a company or a symbol"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
              toFirstPage()
            }}
            onClear={() => {
              setTerm('')
              toFirstPage()
            }}
            className="w-full px-3 sm:w-70"
          />
          <UpdatingNote when={isUpdating} />
          <span aria-hidden="true" className="hidden flex-1 sm:block" />
          {sortedSectors.length === 0 ? null : (
            <SectorSelect
              sectors={sortedSectors}
              value={sector ?? ALL_SECTORS}
              onChange={(code) => {
                pickSector(code === ALL_SECTORS ? null : code)
              }}
            />
          )}
          <MonthPicker periods={periods} value={appliedPeriod} onChange={pickPeriod} />
        </div>

        {holdingsFailed ? (
          <EmptyState
            kind={holdingsView}
            size="page"
            onRetry={() => void holdingsQuery.refetch()}
            retrying={holdingsQuery.isFetching}
            meta={
              holdingsView === 'failed' ? lastUpdatedMeta(holdingsQuery.dataUpdatedAt) : undefined
            }
            {...(holdingsView === 'failed' ? { title: "Couldn't load industry holdings" } : {})}
          />
        ) : holdingsEmpty ? (
          <EmptyState
            kind="empty"
            size="page"
            title="No holdings for this period"
            description="Funds file their sector breakdown monthly — try a different holdings period."
          />
        ) : (
          <RefreshRegion updating={companiesQuery.isPlaceholderData}>
            <CompaniesTable
              rows={companies}
              mode={allSectors ? 'all' : 'sector'}
              onOpenHolders={holdersLive ? setHoldersOf : undefined}
              page={page}
              sectorLabel={sectorLabel}
              view={companiesView === 'empty' ? 'loaded' : companiesView}
              onRetry={() => void companiesQuery.refetch()}
              retrying={companiesQuery.isFetching}
              savedAt={companiesQuery.dataUpdatedAt}
              pageSize={search.pageSize}
              sorting={sorting}
              onSortingChange={onSortingChange}
              query={searching ? term : undefined}
            />
            {companies.length === 0 ? null : (
              <Pagination
                page={page}
                pageSize={search.pageSize}
                total={companies.length}
                unit="companies"
                onPageChange={(next) => {
                  void navigate({ search: (prev) => ({ ...prev, page: next }) })
                }}
                onPageSizeChange={(next) => {
                  void navigate({
                    search: (prev) => ({
                      ...prev,
                      pageSize: toPageSize(next, prev.pageSize),
                      page: 1,
                    }),
                  })
                }}
              />
            )}
          </RefreshRegion>
        )}
      </div>

      <StockHoldersDialog
        symbol={holdersOf}
        period={periodParam}
        onClose={() => {
          setHoldersOf(undefined)
        }}
      />
    </ScreenLayout>
  )
}
